// FTEDN — Community helpers (validation, referral, leaderboard, time formatting)

export type PostType = 'discussion' | 'question' | 'announcement';

/** Validate a new post before submission */
export function validatePost(content: string): { valid: true } | { valid: false; error: string } {
  const trimmed = content.trim();
  if (!trimmed) return { valid: false, error: 'Post cannot be empty' };
  if (trimmed.length < 5) return { valid: false, error: 'Post must be at least 5 characters' };
  if (content.length > 2000) return { valid: false, error: 'Post must be under 2000 characters' };
  return { valid: true };
}

/** Generate a referral link from user ID */
export function generateReferralLink(userId: string, baseUrl: string = 'https://app.ftedn.com'): string {
  const code = btoa(userId).replace(/[=+/]/g, '').slice(0, 8).toUpperCase();
  return `${baseUrl}/join?ref=${code}`;
}

/** Extract referral code from a link */
export function extractReferralCode(link: string): string | null {
  try {
    const url = new URL(link);
    return url.searchParams.get('ref');
  } catch {
    // Try extracting from path-like strings
    const match = link.match(/ref=([A-Z0-9]+)/i);
    return match ? match[1] : null;
  }
}

/** Format relative time from ISO string */
export function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  if (isNaN(then)) return '';

  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  level: number;
  xp: number;
  streak: number;
  reviewCount: number;
}

/** Sort and rank users for the leaderboard */
export function buildLeaderboard(
  users: { name: string; xp: number; level: number; streak: number; reviewCount: number }[],
  sortBy: 'xp' | 'streak' | 'reviews' = 'xp',
  limit: number = 10,
): LeaderboardEntry[] {
  const sorted = [...users].sort((a, b) => {
    if (sortBy === 'xp') return b.xp - a.xp;
    if (sortBy === 'streak') return b.streak - a.streak;
    return b.reviewCount - a.reviewCount;
  });

  return sorted.slice(0, limit).map((u, i) => ({
    rank: i + 1,
    name: u.name,
    level: u.level,
    xp: u.xp,
    streak: u.streak,
    reviewCount: u.reviewCount,
  }));
}

export interface TribeStats {
  totalInvites: number;
  pendingInvites: number;
  activeMembers: number;
  xpEarned: number;
}

/** Calculate tribe stats from referral data */
export function calculateTribeStats(
  referrals: { status: string; xpEarned?: number }[]
): TribeStats {
  return {
    totalInvites: referrals.length,
    pendingInvites: referrals.filter(r => r.status === 'pending').length,
    activeMembers: referrals.filter(r => r.status === 'active' || r.status === 'claimed').length,
    xpEarned: referrals.reduce((sum, r) => sum + (r.xpEarned ?? 0), 0),
  };
}
